<script setup lang="ts">
import { watch } from 'vue'
import { RouterView } from 'vue-router'
import { useI18n } from 'vue-i18n'
import ToastHost from '@/components/common/ToastHost.vue'
import ConfirmHost from '@/components/common/ConfirmHost.vue'
import SkipLink from '@/components/common/SkipLink.vue'
import A11yLiveRegion from '@/components/common/A11yLiveRegion.vue'
import { useOnlineStatus } from '@/composables/useOnlineStatus'
import { useA11yStore } from '@/stores/a11y'

// Racine volontairement nue : elle ne porte que le point de montage des routes
// et les surfaces globales (toasts, confirmations, bannière hors ligne). Toute
// la chrome — barre, menu latéral — vit dans `layouts/AppLayout.vue`, ce qui
// permet à l'écran de connexion de s'afficher plein cadre sans masquer un menu.

const { t } = useI18n()
const a11y = useA11yStore()
const enLigne = useOnlineStatus()

// Annoncé à chaque TRANSITION, pas au montage : un utilisateur qui DÉMARRE hors
// ligne voit la bannière, et une annonce « Hors ligne » au chargement avant tout
// contenu n'apprend rien d'actionnable. Le changement en cours de session, lui,
// explique d'un coup pourquoi les boutons cessent de répondre.
watch(enLigne, (nouveau, ancien) => {
  if (ancien === undefined || nouveau === ancien) return
  a11y.annoncer(t(nouveau ? 'network.online' : 'network.offline'), 'assertive')
})
</script>

<template>
  <!-- Le lien d'évitement est le PREMIER élément du document : c'est ce qui le
       rend atteignable par la première tabulation. -->
  <SkipLink />
  <RouterView />
  <!-- Bannière visuelle seule : l'annonce passe par la région « live »
       (A11yLiveRegion, plus bas) — un bandeau ajouté au DOM AVEC son texte ne
       serait jamais annoncé, et doubler la région ferait lire le message deux
       fois. -->
  <div v-if="!enLigne" class="offline-banner" role="status">
    {{ t('network.offline') }}
  </div>
  <ToastHost />
  <ConfirmHost />
  <A11yLiveRegion />
</template>

<style scoped>
.offline-banner {
  position: fixed;
  top: 0;
  inset-inline: 0;
  z-index: var(--z-toast);
  padding: var(--space-2) var(--space-4);
  text-align: center;
  font-size: var(--text-sm);
  color: var(--color-text-on-solid);
  background: var(--color-warn);
}
</style>
